import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Typography, Box, Select as MuiSelect, MenuItem } from '@mui/material'
import Fields from '../Dashboard/Fields';
import { ExpandMore } from '@mui/icons-material';
import { isAnswered, evaluateCondition } from '../../../utils/questionnaireSupport';
import TextEditor from '../../common/textfield/TextEditor';
import TextInput from '../../common/textfield/TextInput';

/** MUI menu grows to content width by default; pin Paper + Popper to this wrapper’s width so long options wrap inside the questionnaire column. */
const QuestionnaireSelectField = ({ formik, fieldPath, options, readOnly, instanceKey }) => {
  const wrapRef = useRef(null);
  const [menuWidthPx, setMenuWidthPx] = useState(null);

  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const w = Math.round(el.getBoundingClientRect().width);
    if (w > 0) setMenuWidthPx(w);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, fieldPath, options]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const paperSx =
    menuWidthPx != null
      ? {
          width: `${menuWidthPx}px`,
          maxWidth: `${menuWidthPx}px`,
          minWidth: `${menuWidthPx}px !important`,
          boxSizing: 'border-box',
          maxHeight: '300px',
          overflowX: 'hidden',
          overflowY: 'auto',
        }
      : {
          maxWidth: '100%',
          boxSizing: 'border-box',
          maxHeight: '300px',
          overflowX: 'hidden',
          overflowY: 'auto',
        };

  return (
    <Box
      ref={wrapRef}
      sx={{
        width: '100%',
        maxWidth: '100%',
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <MuiSelect
        key={instanceKey}
        {...formik.getFieldProps(fieldPath)}
        displayEmpty
        fullWidth
        inputProps={{ readOnly }}
        onOpen={() => {
          measure();
          requestAnimationFrame(measure);
        }}
        onClose={() => {
          measure();
        }}
        renderValue={(selected) =>
          selected ? (
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{selected}</span>
          ) : (
            <span style={{ color: '#999' }}>Select</span>
          )
        }
        sx={{
          backgroundColor: '#f6f6f6',
          borderRadius: '10px',
          minHeight: '60px',
          height: 'auto',
          width: '100%',
          maxWidth: '100%',
          fontSize: '18px',
          fontWeight: 400,
          pointerEvents: readOnly ? 'none' : 'all',
          cursor: readOnly ? 'auto' : 'pointer',
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': {
            padding: '5px 15px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
        }}
        MenuProps={{
          disableScrollLock: true,
          disablePortal: true,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: {
            style:
              menuWidthPx != null
                ? {
                    width: menuWidthPx,
                    maxWidth: menuWidthPx,
                    minWidth: menuWidthPx,
                    boxSizing: 'border-box',
                    maxHeight: 300,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                  }
                : { maxHeight: 300, overflowX: 'hidden', overflowY: 'auto' },
            sx: {
              mt: 0.5,
              ...paperSx,
              '& .MuiMenuItem-root': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                fontSize: '18px',
                fontWeight: 400,
                alignItems: 'flex-start',
              },
            },
          },
          PopperProps: {
            strategy: 'absolute',
            sx:
              menuWidthPx != null
                ? {
                    width: `${menuWidthPx}px`,
                    maxWidth: `${menuWidthPx}px`,
                    minWidth: `${menuWidthPx}px`,
                    boxSizing: 'border-box',
                  }
                : { maxWidth: '100%', boxSizing: 'border-box' },
            modifiers: [
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  altAxis: true,
                  padding: 8,
                  boundary: 'clippingParents',
                },
              },
            ],
          },
        }}
      >
        <MenuItem value="">
          <em>Select</em>
        </MenuItem>
        {options?.map(({ value, name }, i) => (
          <MenuItem key={i} value={value}>
            {name}
          </MenuItem>
        ))}
      </MuiSelect>
    </Box>
  );
};


const QuestionnaireRenderer = ({
  formik,
  questionResponse,
  questionnaire,
  parentIndex,
  readOnly = false
}) => {
  const [visibleQuestions, setVisibleQuestions] = useState([]);

  useEffect(() => {
    const visibility = questionResponse.map((respItem, idx) => {
      const questionnaireItem = questionnaire?.item?.find(q => q.id === respItem.questionId);
      return questionnaireItem?.enableWhen
        ? evaluateCondition(questionnaireItem.enableWhen.expression, questionResponse)
        : true;
    });
    
    // Clear answers for hidden questions
    visibility.forEach((isVisible, idx) => {
      if (!isVisible && questionResponse[idx]) {
        const currentItem = questionResponse[idx];
        
        // Only clear if answer exists and is not already empty
        if (currentItem.answer && currentItem.answer.length > 0) {
          const needsReset = currentItem.answer.some(ans => 
            (ans.answer !== "" && ans.answer !== null && ans.answer !== undefined) || 
            ans.valueBoolean === true
          );
          
          if (needsReset) {
            // Create empty answers based on question type
            let emptyAnswers;
            
            switch (currentItem.questionType) {
              case "radio":
              case "checkbox":
                // For radio/checkbox, set all valueBoolean to false
                emptyAnswers = currentItem.answer.map(ans => ({
                  ...ans,
                  answer: "",
                  valueBoolean: false
                }));
                break;
                
              case "dropDown":
              case "freeText":
              case "singleFreeText":
              default:
                // For text fields and dropdowns, clear the answer string
                emptyAnswers = currentItem.answer.map(ans => ({
                  ...ans,
                  answer: ""
                }));
                break;
            }
            
            // Update formik with cleared answers
            formik.setFieldValue(`questionResponse[${idx}].answer`, emptyAnswers);
          }
        }
      }
    });
    
    setVisibleQuestions(visibility);
  }, [questionResponse, questionnaire]);

  return questionResponse?.map((item, index) => {
    const isEnabled = visibleQuestions[index];
    if (!isEnabled) {
      return null; // Do not render if the condition is false
    }

    return (
      <Accordion 
        key={index}
        defaultExpanded={true}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            {item?.question}
            {item?.required ? <span style={{ color: "red" }}> *</span> : null}
            {item?.required ? formik.errors.isQuestionsError && !isAnswered(item) && (
              <div className="error-message" style={{ color: "red", fontSize: "12px" }}>
                Mandatory Question
              </div>
            ) : null}
          </Typography>
        </AccordionSummary>

        {/* freeText question type */}
        {item.questionType === "freeText" && (
          <AccordionDetails sx={{ padding: 0 }}>
            <TextEditor
              key={`text-editor-${parentIndex}-${index}`}
              keyField={`questionResponse[${index}].answer[0].answer`}
              formik={formik}
              defaultValue={item?.answer?.[0]?.answer || ""}
              label={false}
              readOnly={readOnly}
            />
          </AccordionDetails>
        )}

        {/* singleFreeText question type */}
        {item.questionType === "singleFreeText" && (
          <AccordionDetails sx={{ padding: 0 }}>
            <TextInput
              key={`text-input-${parentIndex}-${index}`}
              keyField={`questionResponse[${index}].answer[0].answer`}
              formik={formik}
              defaultValue={item?.answer?.[0]?.answer || ""}
              label={false}
              readOnly={readOnly}
              placeholder="Start writing..."
            />
          </AccordionDetails>
        )}

        {/* radio or checkbox question types */}
        {(item.questionType === "radio" || item.questionType === "checkbox") && (
          <AccordionDetails sx={{ padding: 0 }}>
            <Fields
              question={item}
              formik={formik}
              Questions={questionResponse}
              propIndex={index}
              hideMandatory={true}
              readOnly={readOnly}
            />
          </AccordionDetails>
        )}

        {/* dropDown — MUI Select; menu width pinned to wrapper so long options wrap inside the panel */}
        {item.questionType === "dropDown" && (
          <AccordionDetails sx={{ padding: 0, overflow: 'visible' }}>
            <Box className="field-wrap" sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <QuestionnaireSelectField
                formik={formik}
                fieldPath={`questionResponse[${index}].answer[0].answer`}
                options={item.answerOption}
                readOnly={readOnly}
                instanceKey={`dropdown-editor-${parentIndex}-${index}`}
              />
            </Box>
          </AccordionDetails>
        )}
      </Accordion>
    )
  })
}

export default QuestionnaireRenderer;